import { useState } from "react";
import { ChevronRight, Sparkles, Star, Clock, Move, ArrowRight, Calendar as CalendarIcon, ListTodo, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import LoginDialog from "@/components/LoginDialog";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const [buying, setBuying] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  async function buyNow() {
    if (buying) return;
    setBuying(true);
    try {
      const { data } = await axios.post(
        `${API}/payments/checkout`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error("Impossibile avviare il pagamento");
    } catch (err) {
      console.error("checkout failed:", err);
      toast.error("Errore nel pagamento. Riprova.");
    } finally {
      setBuying(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#FDFDFB]">
      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-[#FDFDFB]/95 backdrop-blur border-b border-[#E4E7E1]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="inline-flex items-center gap-3">
            <span className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden ring-1 ring-[#E4E7E1] bg-[#A3B897]/25">
              <img src="/logo.jpg" alt="" className="w-10 h-10 object-cover" />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-serif-display font-semibold text-[20px] text-[#3B443D]">Tempo leggero</span>
              <span className="text-[11px] text-[#6A7368] italic">un'idea di Silvia Lanfranchi</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setLoginOpen(true)}
              className="text-sm text-[#58635B] hover:text-[#3B443D] underline underline-offset-4 decoration-[#A3B897]/60 hover:decoration-[#8FA087] transition-colors"
              data-testid="landing-login-link"
            >
              Accedi
            </button>
            <button
              onClick={buyNow}
              disabled={buying}
              className="tl-btn-primary hidden md:inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#A3B897] hover:bg-[#8FA087] text-white text-sm font-medium transition-all disabled:opacity-60"
              data-testid="landing-nav-buy"
            >
              {buying ? "Attendere…" : "Acquista €11"}
              <ArrowRight size={14} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-10 pt-16 pb-8">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="tl-label text-[#8FA087]">Un planner leggero</div>
            <h1 className="mt-3 font-serif-display font-semibold text-5xl md:text-6xl leading-[1.05] text-[#3B443D]">
              Fai le cose giuste,<br/>
              <span className="italic text-[#8FA087]">con calma</span>.
            </h1>
            <p className="mt-5 text-lg text-[#58635B] max-w-md">
              Un planner giornaliero e settimanale che ti aiuta a scegliere le tue tre priorità, seguirne il tempo con un cronometro e chiudere la giornata con un piccolo diario.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={buyNow}
                disabled={buying}
                className="tl-btn-primary inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#A3B897] hover:bg-[#8FA087] text-white text-base font-medium transition-all disabled:opacity-60"
                data-testid="landing-cta-buy"
              >
                {buying ? "Attendere…" : "Acquista ora — €11"}
                <ArrowRight size={18} strokeWidth={1.75} />
              </button>
            </div>
            <div className="mt-3 text-[13px] text-[#6A7368]">
              Nessun abbonamento · una tantum · aggiornamenti futuri inclusi
            </div>
          </div>

          {/* Preview card */}
          <div className="relative">
            <div className="tl-paper p-6 relative" style={{ transform: "rotate(-0.6deg)" }}>
              <div className="tl-label">Domenica 9 agosto</div>
              <div className="mt-2 font-serif-display italic text-[#8FA087]">
                “Meno cose, fatte meglio.”
              </div>
              <div className="mt-4 flex flex-col gap-2">
                <MiniTask done title="Registra il podcast" cat="Lavoro" color="#9BA3B5" star />
                <MiniTask title="Innaffia le orchidee" cat="Casa" color="#CBA38D" star />
                <MiniTask title="Chiama la mamma" cat="Personale" color="#D4B5B0" />
                <MiniTask title="Camminata al parco" cat="Salute" color="#A3B899" />
              </div>
              <div className="mt-4 pt-3 border-t border-[#E4E7E1] flex items-center gap-2 text-xs text-[#6A7368]">
                <Star size={12} className="text-[#D4A94A]" fill="#D4A94A" />
                2/3 priorità del giorno
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 tl-paper px-4 py-3 flex items-center gap-2" style={{ transform: "rotate(1.5deg)" }}>
              <Clock size={14} className="text-[#8FA087]" strokeWidth={1.75} />
              <span className="text-xs text-[#58635B]">In corso · 12:04 effettivi</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-10 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          <Feature icon={<Star size={18} strokeWidth={1.5} className="text-[#D4A94A]" />} title="Le tre priorità del giorno" desc="Scegli solo tre cose che contano davvero. Tempo leggero ti impedisce di scegliere la quarta." />
          <Feature icon={<Clock size={18} strokeWidth={1.5} className="text-[#8FA087]" />} title="Cronometro integrato" desc="Un clic, e sai davvero quanto tempo passi su cosa. Nessun cronometro separato, nessuna stima a occhio." />
          <Feature icon={<Move size={18} strokeWidth={1.5} className="text-[#8FA087]" />} title="Trascina, sposta, respira" desc="Rimanda a domani, sposta al pomeriggio, allunga la durata: tutto con un semplice trascinamento." />
        </div>
      </section>

      {/* Screenshots showcase */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-6">
        <div className="text-center mb-8">
          <div className="tl-label">Uno sguardo dentro</div>
          <h2 className="mt-2 font-serif-display font-semibold text-3xl md:text-4xl text-[#3B443D]">
            Il tuo planner, ogni giorno.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <figure className="tl-paper p-3 overflow-hidden max-w-[340px] md:max-w-none mx-auto w-full">
            <picture>
            <source media="(max-width: 767px)" srcSet="/shot-day-mobile.jpg" />
            <img
              src="/shot-day.jpg"
              alt="Vista giornaliera di Tempo leggero: priorità, cronometro e calendario"
              className="w-full h-auto rounded-md block"
              loading="lazy"
            />
            </picture>
            <figcaption className="mt-3 text-center text-xs text-[#6A7368] italic">Vista giornaliera — priorità, cronometro, calendario e diario</figcaption>
          </figure>
          <figure className="tl-paper p-3 overflow-hidden max-w-[340px] md:max-w-none mx-auto w-full">
            <picture>
            <source media="(max-width: 767px)" srcSet="/shot-week-mobile.jpg" />
            <img
              src="/shot-week.jpg"
              alt="Vista settimanale di Tempo leggero come un planner cartaceo"
              className="w-full h-auto rounded-md block"
              loading="lazy"
            />
            </picture>
            <figcaption className="mt-3 text-center text-xs text-[#6A7368] italic">Vista settimanale — griglia oraria come un planner cartaceo</figcaption>
          </figure>
        </div>
      </section>

      {/* Pricing highlight */}
      <section className="max-w-[900px] mx-auto px-6 md:px-10 py-6">
        <div className="tl-paper p-8 md:p-10 text-center bg-white">
          <div className="tl-label">Prezzo di lancio</div>
          <div className="mt-3 font-serif-display font-semibold text-6xl text-[#3B443D]">€11</div>
          <div className="mt-1 text-[13px] text-[#6A7368]">una tantum · IVA inclusa · pagamento sicuro Stripe</div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-left max-w-xl mx-auto">
            <Perk text="Planner giornaliero e settimanale illimitato" />
            <Perk text="Priorità, cronometro, trascina e rilascia" />
            <Perk text="Diario e frasi motivazionali quotidiane" />
            <Perk text="Sincronizzazione su qualsiasi dispositivo" />
            <Perk text="Backup e export dei dati" />
            <Perk text="Aggiornamenti futuri sempre inclusi" strong />
          </div>

          <div className="mt-6 inline-block px-4 py-2 rounded-full bg-[#F3F5F0] text-xs text-[#58635B]" data-testid="landing-earlybird">
            Chi acquista ora paga il prezzo più basso di sempre e avrà accesso a tutti gli aggiornamenti futuri.
          </div>

          <div className="mt-8">
            <button
              onClick={buyNow}
              disabled={buying}
              className="tl-btn-primary inline-flex items-center gap-2 px-10 py-4 rounded-full bg-[#A3B897] hover:bg-[#8FA087] text-white text-base font-medium transition-all disabled:opacity-60"
              data-testid="landing-buy-hero"
            >
              Acquista ora
              <ChevronRight size={18} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-10 py-16">
        <div className="text-center">
          <div className="tl-label">In arrivo</div>
          <h2 className="mt-2 font-serif-display font-semibold text-3xl md:text-4xl text-[#3B443D]">
            E poi cresce con te.
          </h2>
          <p className="mt-2 text-[#58635B]">Chi acquista adesso riceve queste integrazioni senza pagare un centesimo in più.</p>
        </div>
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <RoadmapCard
            icon={<CalendarIcon size={20} strokeWidth={1.5} className="text-[#8FA087]" />}
            title="Google Calendar"
            desc="Gli eventi del tuo Google Calendar appariranno automaticamente nel settimanale. Vedi lavoro e vita in un unico posto."
          />
          <RoadmapCard
            icon={<ListTodo size={20} strokeWidth={1.5} className="text-[#8FA087]" />}
            title="Attività da Notion"
            desc="Importa le attività dalle tue pagine Notion: aggiungili al planner con un clic e non perdere più un impegno."
          />
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-[900px] mx-auto px-6 md:px-10 pb-24">
        <div className="text-center">
          <div className="font-serif-display italic text-xl text-[#8FA087]">
            “Un passo alla volta è comunque avanti.”
          </div>
          <div className="mt-6">
            <button
              onClick={buyNow}
              className="tl-btn-primary inline-flex items-center gap-2 px-10 py-4 rounded-full bg-[#A3B897] hover:bg-[#8FA087] text-white text-base font-medium transition-all"
              data-testid="landing-buy-footer"
            >
              Sblocca Tempo leggero — €11
              <ArrowRight size={18} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </section>

      <footer className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 text-center text-xs text-[#6A7368] font-serif-display italic border-t border-[#E4E7E1]">
        Tempo leggero — un'idea di Silvia Lanfranchi
      </footer>
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="tl-paper p-6">
      <div className="w-10 h-10 rounded-full bg-[#F3F5F0] flex items-center justify-center">{icon}</div>
      <h3 className="mt-4 font-serif-display text-xl text-[#3B443D]">{title}</h3>
      <p className="mt-2 text-sm text-[#58635B] leading-relaxed">{desc}</p>
    </div>
  );
}

function Perk({ text, strong = false }) {
  return (
    <div className="flex items-start gap-2">
      <span className="w-5 h-5 rounded-full bg-[#A3B897] flex items-center justify-center flex-shrink-0 mt-0.5">
        <Check size={12} strokeWidth={2.5} className="text-white" />
      </span>
      <span className={`text-sm ${strong ? "font-semibold text-[#3B443D]" : "text-[#58635B]"}`}>{text}</span>
    </div>
  );
}

function RoadmapCard({ icon, title, desc }) {
  return (
    <div className="tl-paper p-6 flex gap-4">
      <div className="w-12 h-12 rounded-full bg-[#F3F5F0] flex items-center justify-center flex-shrink-0">{icon}</div>
      <div>
        <div className="tl-label">Presto</div>
        <h3 className="mt-1 font-serif-display text-2xl text-[#3B443D]">{title}</h3>
        <p className="mt-1 text-sm text-[#58635B]">{desc}</p>
      </div>
    </div>
  );
}

function MiniTask({ title, cat, color, done = false, star = false }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`w-4 h-4 rounded-full border ${done ? "bg-[#A3B897] border-[#A3B897]" : "border-[#6A7368]"} flex items-center justify-center flex-shrink-0`}>
        {done && <Check size={9} strokeWidth={3} className="text-white" />}
      </span>
      <span className={`text-[13px] flex-1 ${done ? "tl-strike" : "text-[#3B443D]"}`}>{title}</span>
      <span className="tl-chip" style={{ background: color + "26", color: "#58635B" }}>
        <span className="tl-chip-dot" style={{ background: color }} />
        {cat}
      </span>
      {star && <Star size={12} className="text-[#D4A94A]" fill="#D4A94A" />}
    </div>
  );
}
